import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { listMyTickets } from '../../services/supportService';
import Logo from '../ui/Logo';

interface SidebarProps {
  onClose?: () => void;
}

export default function Sidebar({ onClose }: SidebarProps) {
  const location = useLocation();
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const isAO = user?.role === 'AO';
  const isCOMMERCE = user?.role === 'COMMERCE';
  const isADMIN = user?.role === 'ADMIN';

  useEffect(() => {
    if (!isADMIN) {
      loadUnreadCount();
      const interval = setInterval(loadUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [isADMIN]);

  const loadUnreadCount = async () => {
    try {
      const tickets = await listMyTickets();
      const count = tickets.reduce((sum, t) => sum + (t.unreadCount || 0), 0);
      setUnreadCount(count);
    } catch {
      setUnreadCount(0);
    }
  };

  const adminLinks = [
    { to: '/admin', icon: 'ri-dashboard-line', label: 'Vue d\'ensemble' },
    { to: '/admin/qa', icon: 'ri-user-search-line', label: 'Console QA' },
    { to: '/admin/utilisateurs', icon: 'ri-user-line', label: 'Utilisateurs' },
    { to: '/admin/apporteurs', icon: 'ri-user-star-line', label: 'Apporteurs' },
    { to: '/admin/collaborateurs', icon: 'ri-user-settings-line', label: 'Collaborateurs' },
    { to: '/admin/candidatures', icon: 'ri-file-list-line', label: 'Candidatures' },
    { to: '/admin/societes', icon: 'ri-building-line', label: 'Sociétés' },
    { to: '/admin/crm', icon: 'ri-line-chart-line', label: 'CRM' },
    { to: '/admin/classement', icon: 'ri-trophy-line', label: 'Classement' },
    { to: '/admin/remuneration', icon: 'ri-money-euro-circle-line', label: 'Rémunération AO' },
    { to: '/admin/statistiques', icon: 'ri-bar-chart-line', label: 'Statistiques' },
    { to: '/admin/logs', icon: 'ri-file-text-line', label: 'Logs' },
    { to: '/admin/support', icon: 'ri-customer-service-line', label: 'Support', badge: unreadCount },
    { to: '/admin/documents', icon: 'ri-folder-line', label: 'Documents' },
  ];

  const aoLinks = [
    { to: '/dashboard/ao/overview', icon: 'ri-dashboard-line', label: 'Vue d\'ensemble' },
    { to: '/dashboard/ao/opportunites', icon: 'ri-building-line', label: 'Mes recommandations' },
    { to: '/dashboard/ao/classement', icon: 'ri-trophy-line', label: 'Classement' },
    { to: '/dashboard/ao/affilies', icon: 'ri-team-line', label: 'Affiliés' },
    { to: '/dashboard/ao/commissions', icon: 'ri-money-euro-circle-line', label: 'Commissions' },
    { to: '/dashboard/ao/parrainage', icon: 'ri-share-line', label: 'Parrainage' },
    { to: '/dashboard/notifications', icon: 'ri-notification-3-line', label: 'Notifications' },
    { to: '/dashboard/compte', icon: 'ri-user-line', label: 'Mon compte' },
    { to: '/dashboard/support', icon: 'ri-customer-service-line', label: 'Support', badge: unreadCount },
  ];

  const commerceLinks = [
    { to: '/dashboard/collaborateur/overview', label: 'Vue d\'ensemble', icon: 'ri-dashboard-line' },
    { to: '/dashboard/collaborateur/marques', label: 'Marques partenaires', icon: 'ri-building-line' },
    { to: '/dashboard/collaborateur/rendez-vous', label: 'Prendre RDV', icon: 'ri-calendar-line' },
    { to: '/dashboard/collaborateur/documents', label: 'Documents', icon: 'ri-folder-open-line' },
    { to: '/dashboard/notifications', label: 'Notifications', icon: 'ri-notification-3-line' },
    { to: '/dashboard/support', label: 'Support', icon: 'ri-customer-service-line', badge: unreadCount },
    { to: '/dashboard/compte', label: 'Mon compte', icon: 'ri-user-settings-line' },
  ];

  const links = isADMIN ? adminLinks : isAO ? aoLinks : isCOMMERCE ? commerceLinks : [];

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'VALIDATED': return 'bg-green-500';
      case 'FORM_SUBMITTED': return 'bg-yellow-500';
      case 'REJECTED': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getRoleLabel = (role?: string) => {
    switch (role) {
      case 'AO': return 'Apporteur';
      case 'COMMERCE': return 'Commerçant';
      case 'ADMIN': return 'Admin';
      default: return 'Utilisateur';
    }
  };

  return (
    <aside className="w-64 bg-[#111111] border-r border-gray-800 flex flex-col h-full">
      <div className="p-6 border-b border-gray-800 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <Logo className="w-8 h-8" />
          <span className="text-xl font-bold text-white">TP13</span>
        </Link>
        {onClose && (
          <button
            onClick={onClose}
            className="md:hidden w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-800 transition-colors"
          >
            <i className="ri-close-line text-xl text-gray-400"></i>
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-6">
        <nav className="space-y-1 px-3">
          {links.map((link) => {
            const isActive = location.pathname === link.to;
            return (
              <Link
                key={link.to}
                to={link.to}
                onClick={onClose}
                className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors relative ${
                  isActive
                    ? 'bg-[#DC143C] text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
              >
                <i className={`${link.icon} text-lg`}></i>
                <span className="font-medium flex-1">{link.label}</span>
                {link.badge && link.badge > 0 && (
                  <span className="px-2 py-0.5 bg-[#DC143C] text-white text-xs font-medium rounded-full">
                    {link.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
      </div>

      {!isADMIN && (
        <div className="p-4 border-t border-gray-800 space-y-3">
          <div className="flex items-center gap-2 px-2">
            <div className={`w-2 h-2 rounded-full ${getStatusColor(user?.status)}`}></div>
            <span className="text-xs text-gray-400">{getRoleLabel(user?.role)}</span>
          </div>
          
          <Link
            to="/logout"
            className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors w-full"
          >
            <i className="ri-logout-box-line text-lg"></i>
            <span className="font-medium">Se déconnecter</span>
          </Link>
        </div>
      )}
    </aside>
  );
}